import { signupContext } from './signupContext';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ACCOUNT_API_ENDPOINT } from './config';
import { useAuth } from './AuthContext';
import type { AccessStatus } from '../properties/api';
import type { AccountRole } from '../properties/api';

export interface Profile {
  userId: string;
  email: string;
  name: string;
  company: string;
  phone: string;
  website: string;
  plan: string;
  accessStatus: AccessStatus;
  role: AccountRole;
  // Decided server-side from the Cognito group, so the console link only shows
  // for an account the admin API would actually answer.
  isAdmin: boolean;
  delegatedAdmin?: boolean;
  adminPermissions?: string[];
  studioAccess?: boolean;
  storageBytes: number;
  storageQuotaBytes: number;
  aiEditsEnabled: boolean;
  suspended: boolean;
  hasAvatar: boolean;
  avatarUrl: string | null;
  hasLogo: boolean;
  logoUpdatedAt: string | null;
  logoUrl: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export type EditableProfile = Pick<Profile, 'name' | 'company' | 'phone' | 'website'>;

interface ProfileContextValue {
  profile: Profile | null;
  loading: boolean;
  error: string;
  refresh: () => Promise<Profile | null>;
  save: (changes: Partial<EditableProfile>) => Promise<Profile>;
  uploadAvatar: (file: File, onProgress?: (fraction: number) => void) => Promise<Profile | null>;
  removeAvatar: () => Promise<Profile>;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

const AVATAR_SIZE = 512;
const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const readJson = async <T,>(response: Response): Promise<T> => {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
    } catch {
      // keep the status-based message
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
};

// Phones hand over 5-10 MB photos of any shape; a centred 512 px square is all
// an avatar ever shows, so it is cut down before it leaves the browser.
const squareAvatar = async (file: File): Promise<Blob> => {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Your browser could not prepare this photo.');
  context.drawImage(
    bitmap,
    (bitmap.width - side) / 2,
    (bitmap.height - side) / 2,
    side,
    side,
    0,
    0,
    AVATAR_SIZE,
    AVATAR_SIZE
  );
  bitmap.close?.();
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Your browser could not prepare this photo.'))),
      'image/jpeg',
      0.9
    )
  );
};

const postToStorage = (
  upload: { url: string; fields: Record<string, string> },
  blob: Blob,
  onProgress?: (fraction: number) => void
) =>
  new Promise<void>((resolve, reject) => {
    const form = new FormData();
    Object.entries(upload.fields).forEach(([name, value]) => form.append(name, value));
    form.append('file', blob);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', upload.url);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload rejected by storage (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    xhr.send(form);
  });

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { status, authFetch } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async (): Promise<Profile | null> => {
    if (!ACCOUNT_API_ENDPOINT) {
      setError('The account API is not configured for this build.');
      return null;
    }
    setLoading(true);
    setError('');
    try {
      const next = await readJson<Profile>(await authFetch(`/me?${new URLSearchParams({ timeZone: signupContext().timeZone, language: signupContext().language })}`));
      setProfile(next);
      return next;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not load your profile.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (status === 'signedIn') {
      refresh();
    } else if (status === 'signedOut') {
      setProfile(null);
      setError('');
    }
  }, [refresh, status]);

  const value = useMemo<ProfileContextValue>(
    () => ({
      profile,
      loading,
      error,
      refresh,
      save: async (changes) => {
        const next = await readJson<Profile>(
          await authFetch('/me', { method: 'PUT', body: JSON.stringify(changes) })
        );
        setProfile(next);
        return next;
      },
      uploadAvatar: async (file, onProgress) => {
        if (!AVATAR_TYPES.includes(file.type)) {
          throw new Error('Choose a JPEG, PNG or WebP photo.');
        }
        const blob = await squareAvatar(file);
        const { upload } = await readJson<{ upload: { url: string; fields: Record<string, string> } }>(
          await authFetch('/me/avatar', {
            method: 'POST',
            body: JSON.stringify({ contentType: 'image/jpeg' }),
          })
        );
        await postToStorage(upload, blob, onProgress);
        return refresh();
      },
      removeAvatar: async () => {
        const next = await readJson<Profile>(await authFetch('/me/avatar', { method: 'DELETE' }));
        setProfile(next);
        return next;
      },
    }),
    [authFetch, error, loading, profile, refresh]
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = (): ProfileContextValue => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used inside ProfileProvider');
  }
  return context;
};
